import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'
import {
  Box,
  Heading3,
  Display,
  FlexDirection,
  JustifyContent,
  Overflow,
  ModalClose,
  DisplayPosition,
  Divider,
  Color,
  ZIndex,
} from '@gousto-internal/citrus-react'
import type {
  BasketContentBlock,
  BasketSummaryContentBlock,
  CheckoutState,
  DeliveryDetailsContentBlock,
  NotificationContentBlock,
  UpsellsContentBlock as ApiUpsellsContentBlock,
  UseByRecipeListContentBlock,
} from '@library/api-menu-experience'
import { FlexPortionEditorBoundary } from '@library/menu-page'
import { Recipe, RecipeUUID } from '@library/menu-service'
import { Recipes } from './Recipes'
import { DeliveryDetails, ServerDrivenDeliveryDetails } from './DeliveryDetails'
import { CheckoutButton } from './CheckoutButton'
import { OrderSummary } from './OrderSummary'
import { OrderSummaryPlaceholder } from './OrderSummaryPlaceholder'
import type { DeliveryPricing } from './types'
import { TransactionalOrderBanner } from './TransactionalOrderBanner'
import { BasketUpsellSection } from './BasketUpsell/BasketUpsellSection'
import type {
  BasketUpsellCarouselContent,
  BasketUpsellProduct,
  BasketUpsellProductActionSource,
} from './BasketUpsell/types'
import { Products } from './Products'
import { RecipesGrouped } from './RecipesGrouped'
import { LoadingSkeleton } from './RecipesGrouped/LoadingSkeleton'
import { UpsellsContentBlock } from './BasketUpsell/UpsellsContentBlock'
import {
  type FlexPortionTrackingContext,
  trackClickEditRecipeQuantity,
  trackSelectPortionCount,
  trackUpdatePortion,
} from '../../../../../recipe-tile/src/components/Flex'
import { FlexModalOverlay } from '../../../../../recipe-tile/src/components/Flex/FlexModalOverlay'
import { FlexPortionFooter } from '../../../../../recipe-tile/src/components/Flex/FlexPortionFooter'
import { UndoBanner } from '../UndoBanner/UndoBanner'
import { getGroupedRecipePendingStates } from '../../../models/getGroupedRecipePendingStates'
import { BasketErrorToast, type BasketErrorToastContent } from './BasketErrorToast'
import { BasketNotification } from './BasketNotification'
import { BasketSummary } from './BasketSummary'

type BasketUpsell = {
  carousel: BasketUpsellCarouselContent
  products: BasketUpsellProduct[]
  selectedProducts: BasketUpsellProduct[]
  onAddProduct: (productId: string, source: BasketUpsellProductActionSource) => void
  onClickProduct: (product: BasketUpsellProduct) => void
  onRemoveProduct: (productId: string, source: BasketUpsellProductActionSource) => void
}

type BasketRecipe = {
  recipe: Recipe
  quantity: number
  numPortions: number
}

type BasketRecipeSelection = {
  id: string
  quantity: number
  numPortions: number
}

type BasketContent = {
  basketGifts?: Record<string, number>
  basketProductQuantities: Record<string, number>
  basketRecipes: BasketRecipeSelection[]
  checkout?: CheckoutState
  content?: BasketContentBlock[]
  error?: unknown
  isLoading: boolean
  isValidating: boolean
  onAddProduct: (productId: string, source: BasketUpsellProductActionSource) => void
  onClickProduct: (product: BasketUpsellProduct) => void
  onRemoveProduct: (productId: string, source: BasketUpsellProductActionSource) => void
  pendingProductIds?: ReadonlySet<string>
}

type AddRecipeInfo = {
  position?: string
  source?: 'merch_banner'
  merch_header_type?: 'campaign-banner' | 'compact-campaign-banner'
}

const isUseByRecipeListContentBlock = (
  contentBlock: BasketContentBlock,
): contentBlock is UseByRecipeListContentBlock =>
  contentBlock.type === 'use-by-recipe-list' && 'groups' in contentBlock

const isUpsellsContentBlock = (
  contentBlock: BasketContentBlock,
): contentBlock is ApiUpsellsContentBlock =>
  contentBlock.type === 'upsells' && 'products' in contentBlock

const isNotificationContentBlock = (
  contentBlock: BasketContentBlock,
): contentBlock is NotificationContentBlock =>
  contentBlock.type === 'notification' &&
  'displayType' in contentBlock &&
  'messageHTML' in contentBlock

const isBasketSummaryContentBlock = (
  contentBlock: BasketContentBlock,
): contentBlock is BasketSummaryContentBlock =>
  contentBlock.type === 'basket-summary' && 'sections' in contentBlock

const getBasketSummaryRecipeCount = (contentBlock: BasketSummaryContentBlock) =>
  contentBlock.sections.reduce(
    (count, section) =>
      count + section.lines.filter((line) => line.displayType === 'recipe').length,
    0,
  )

const isDeliveryDetailsContentBlock = (
  contentBlock: BasketContentBlock,
): contentBlock is DeliveryDetailsContentBlock =>
  contentBlock.type === 'delivery-details' && 'date' in contentBlock && 'address' in contentBlock

const BasketContentLoading = () => (
  <Box
    aria-label="Loading your basket"
    data-testid="basket-content-loading"
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    gap={4}
    role="status"
  >
    <LoadingSkeleton width="42%" height="24px" />
    <LoadingSkeleton width="72%" />
    <LoadingSkeleton width="100%" height="32px" />
    {[0, 1].map((item) => (
      <Box
        key={item}
        bg={Color.White}
        borderRadius={1.5}
        display={Display.Flex}
        gap={3}
        paddingH={3}
        paddingV={3}
      >
        <LoadingSkeleton width="88px" height="88px" />
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} flexGrow={1} gap={3}>
          <LoadingSkeleton width="85%" height="24px" />
          <LoadingSkeleton width="55%" />
          <LoadingSkeleton width="35%" />
        </Box>
      </Box>
    ))}
  </Box>
)

const BasketContentMessage = ({
  children,
  testId,
}: {
  children: React.ReactNode
  testId: string
}) => (
  <Box
    bg={Color.White}
    borderRadius={1.5}
    data-testid={testId}
    paddingH={4}
    paddingV={4}
    role="status"
  >
    {children}
  </Box>
)

type ContentProps = {
  name: string
  maxRecipesInBox: number
  numPortions: number
  basketRecipes: BasketRecipe[]
  menuUuid?: string | null
  closeModal: () => void
  addRecipe: (
    id: RecipeUUID,
    view: string,
    recipeInfo?: AddRecipeInfo,
    orderId?: string,
    isOrderRecipe?: boolean,
    numPortions?: number,
  ) => void
  removeRecipe: (id: RecipeUUID, view: string) => void
  updateRecipePortions: (id: RecipeUUID, numPortions: number) => void
  onClickRecipe: (id: RecipeUUID) => void
  onCheckout: (e: SyntheticEvent) => void
  deliverySlot: {
    date: string
    timeStart: string
    timeEnd: string
  }
  deliveryAddress: {
    name?: string
    line1: string
    line2: string
    line3: string
    town: string
    postcode: string
    state: string
    county: string
  }
  deliveryPricing: DeliveryPricing
  isPricingPending?: boolean
  discountLabel?: string | null
  isCheckoutDisabled: boolean
  isCheckoutClientGuardDisabled?: boolean
  isCheckoutPending: boolean
  isTransactionalOrder: boolean
  basketUpsell?: BasketUpsell
  basketContent?: BasketContent
  errorToast?: BasketErrorToastContent | null
  highlightedRecipeId?: RecipeUUID
  onDismissErrorToast?: () => void
  isValueBannerEnabled?: boolean
}

export const Content = ({
  name,
  maxRecipesInBox,
  numPortions,
  basketRecipes,
  menuUuid,
  closeModal,
  addRecipe,
  removeRecipe,
  updateRecipePortions,
  onClickRecipe,
  onCheckout,
  deliverySlot,
  deliveryAddress,
  deliveryPricing,
  isPricingPending = false,
  discountLabel,
  isCheckoutDisabled,
  isCheckoutClientGuardDisabled = false,
  isCheckoutPending,
  isTransactionalOrder,
  basketUpsell,
  basketContent,
  errorToast,
  highlightedRecipeId,
  onDismissErrorToast,
  isValueBannerEnabled = false,
}: ContentProps) => {
  const [lastRemoved, setLastRemoved] = useState<BasketRecipe | null>(null)
  const [editingRecipeId, setEditingRecipeId] = useState<RecipeUUID | null>(null)
  const [dismissedLoadError, setDismissedLoadError] = useState<unknown>(null)
  const bannerTimeoutId = useRef<NodeJS.Timeout | null>(null)

  const clearLastRemoved = () => {
    if (bannerTimeoutId.current) {
      clearTimeout(bannerTimeoutId.current)
      bannerTimeoutId.current = null
    }
    setLastRemoved(null)
  }

  const scheduleAutoHide = () => {
    bannerTimeoutId.current = setTimeout(clearLastRemoved, 3000)
  }

  const handleRemove = (recipe: Recipe) => {
    if (bannerTimeoutId.current) {
      clearTimeout(bannerTimeoutId.current)
    }
    const removedBasketRecipe = basketRecipes.find((item) => item.recipe.id === recipe.id)

    setLastRemoved(removedBasketRecipe ?? { recipe, quantity: 1, numPortions })
    setEditingRecipeId(null)
    scheduleAutoHide()
    removeRecipe(recipe.id, 'modal_basket')
  }

  const handleUndo = () => {
    if (!lastRemoved) {
      return
    }
    addRecipe(
      lastRemoved.recipe.id,
      'modal_basket',
      undefined,
      undefined,
      undefined,
      lastRemoved.numPortions,
    )
    clearLastRemoved()
  }

  useEffect(() => () => clearLastRemoved(), [])
  useEffect(() => {
    if (!errorToast) return

    if (bannerTimeoutId.current) {
      clearTimeout(bannerTimeoutId.current)
      bannerTimeoutId.current = null
    }
    setLastRemoved(null)
  }, [errorToast])

  const numberOfRecipesInBasket = basketRecipes.reduce((total, item) => total + item.quantity, 0)
  const isOverLimitBy = numberOfRecipesInBasket - maxRecipesInBox
  const shouldShowOrderSummary =
    numberOfRecipesInBasket >= 2 && numberOfRecipesInBasket <= maxRecipesInBox
  const editingRecipe = basketRecipes.find(({ recipe }) => recipe.id === editingRecipeId)
  const getTrackingContext = ({
    recipe,
    numPortions: recipeNumPortions,
  }: BasketRecipe): FlexPortionTrackingContext => ({
    basketRecipeCount: numberOfRecipesInBasket,
    menuUuid,
    recipePortionCount: recipeNumPortions,
    recipeUuid: recipe.id,
    source: 'modal_basket_screen',
  })
  const isFlexPortionEditorOpen = Boolean(editingRecipe)
  const isServerDrivenBasket = Boolean(basketContent)
  const loadErrorToast =
    basketContent?.error && !basketContent.content && basketContent.error !== dismissedLoadError
      ? {
          title: 'Something went wrong',
          message: "Couldn't load basket. Please try again.",
        }
      : null
  const visibleErrorToast = errorToast ?? loadErrorToast
  const dismissErrorToast = () => {
    if (errorToast) {
      onDismissErrorToast?.()
    } else if (basketContent?.error) {
      setDismissedLoadError(basketContent.error)
    }
  }

  const closePortionEditor = () => setEditingRecipeId(null)
  const handleEditRecipe = (recipeId: RecipeUUID) => {
    const basketRecipe = basketRecipes.find(({ recipe }) => recipe.id === recipeId)

    if (!basketRecipe) {
      return
    }

    trackClickEditRecipeQuantity(getTrackingContext(basketRecipe))
    setEditingRecipeId(recipeId)
  }
  const handleUpdatePortions = (updatedNumPortions: number) => {
    if (!editingRecipe) {
      return
    }

    const trackingContext = getTrackingContext(editingRecipe)

    updateRecipePortions(editingRecipe.recipe.id, updatedNumPortions)
    trackUpdatePortion(trackingContext, updatedNumPortions)
    closePortionEditor()
  }

  const handleSelectPortions = (updatedNumPortions: number) => {
    if (!editingRecipe) {
      return
    }

    trackSelectPortionCount(getTrackingContext(editingRecipe), updatedNumPortions)
  }

  const handleRemoveEditingRecipe = () => {
    if (!editingRecipe) {
      return
    }

    handleRemove(editingRecipe.recipe)
  }

  const handleRemoveGroupedRecipe = (recipeId: RecipeUUID) => {
    const basketRecipe = basketRecipes.find(({ recipe }) => recipe.id === recipeId)

    if (basketRecipe) handleRemove(basketRecipe.recipe)
  }

  const renderedBasketContent = (() => {
    if (!basketContent) return null

    if (basketContent.error && !basketContent.content) {
      return <BasketContentLoading />
    }

    if (!basketContent.content) {
      return basketContent.isLoading ? (
        <BasketContentLoading />
      ) : (
        <BasketContentMessage testId="basket-content-empty">
          Your basket is ready when you are.
        </BasketContentMessage>
      )
    }

    const supportedContent = basketContent.content.filter(
      (contentBlock) =>
        isUseByRecipeListContentBlock(contentBlock) ||
        isNotificationContentBlock(contentBlock) ||
        isBasketSummaryContentBlock(contentBlock) ||
        isDeliveryDetailsContentBlock(contentBlock) ||
        isUpsellsContentBlock(contentBlock),
    )

    if (!supportedContent.length) {
      return (
        <BasketContentMessage testId="basket-content-empty">
          Your basket is ready when you are.
        </BasketContentMessage>
      )
    }

    const localRecipes = basketContent.basketRecipes.map((recipe) => ({
      ...recipe,
      id: recipe.id as RecipeUUID,
    }))
    const basketRecipeIds = new Set<RecipeUUID>(localRecipes.map(({ id }) => id))
    const pendingRecipeStates = getGroupedRecipePendingStates(
      localRecipes,
      supportedContent.filter(isUseByRecipeListContentBlock),
    )

    return supportedContent.map((contentBlock, index) => {
      if (isNotificationContentBlock(contentBlock)) {
        return (
          <BasketNotification key={`${contentBlock.type}-${index}`} notification={contentBlock} />
        )
      }

      if (isUseByRecipeListContentBlock(contentBlock)) {
        return (
          <RecipesGrouped
            key={`${contentBlock.type}-${index}`}
            basketRecipeIds={basketRecipeIds}
            contentBlock={contentBlock}
            highlightedRecipeId={highlightedRecipeId}
            pendingStates={pendingRecipeStates}
            onClickRecipe={onClickRecipe}
            onEditRecipe={handleEditRecipe}
            onRemoveRecipe={handleRemoveGroupedRecipe}
          />
        )
      }

      if (isUpsellsContentBlock(contentBlock)) {
        return (
          <UpsellsContentBlock
            key={`${contentBlock.type}-${index}`}
            basketGifts={basketContent.basketGifts}
            basketProductQuantities={basketContent.basketProductQuantities}
            block={contentBlock}
            onAddProduct={basketContent.onAddProduct}
            onClickProduct={basketContent.onClickProduct}
            onRemoveProduct={basketContent.onRemoveProduct}
            pendingProductIds={basketContent.pendingProductIds}
          />
        )
      }

      if (isBasketSummaryContentBlock(contentBlock)) {
        if (basketContent.isValidating) {
          return (
            <OrderSummaryPlaceholder
              key={`${contentBlock.type}-${index}`}
              recipeCount={getBasketSummaryRecipeCount(contentBlock)}
            />
          )
        }

        return <BasketSummary key={`${contentBlock.type}-${index}`} contentBlock={contentBlock} />
      }

      if (isDeliveryDetailsContentBlock(contentBlock)) {
        return (
          <ServerDrivenDeliveryDetails
            key={`${contentBlock.type}-${index}`}
            contentBlock={contentBlock}
          />
        )
      }

      return null
    })
  })()

  const isServerCheckoutDisabled =
    !basketContent ||
    basketContent.checkout?.enabled !== true ||
    basketContent.isLoading ||
    basketContent.isValidating ||
    Boolean(basketContent.error) ||
    isCheckoutClientGuardDisabled ||
    isCheckoutPending

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      flexGrow={1}
      height="100%"
      minHeight="0"
      overflowY={Overflow.Hidden}
      position={DisplayPosition.Relative}
    >
      <Box
        flexShrink={0}
        paddingH={5}
        position={DisplayPosition.Relative}
        style={{ paddingTop: '1.2rem' }}
      >
        <ModalClose onClose={closeModal} />
        <Box paddingH={0} paddingBottom={3}>
          <Heading3>{`${name}'s basket`}</Heading3>
        </Box>
      </Box>
      <Divider />
      <Box
        flexGrow={1}
        flexShrink={1}
        minHeight="0"
        overflowY={isFlexPortionEditorOpen ? Overflow.Hidden : Overflow.Scroll}
        paddingV={0}
        paddingH={0}
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          justifyContent={JustifyContent.FlexStart}
          flexGrow={1}
          minHeight={['60vh']}
          paddingH={5}
          paddingV={4}
          gap={6}
          bg={Color.NeutralGrey_50}
        >
          {!isServerDrivenBasket && isTransactionalOrder && <TransactionalOrderBanner />}
          {!basketContent ? (
            <>
              <Recipes
                basketRecipes={basketRecipes}
                removeRecipe={handleRemove}
                onClickRecipe={onClickRecipe}
                onEditRecipe={handleEditRecipe}
                numberOfRecipesInBasket={numberOfRecipesInBasket}
                deliveryDate={deliverySlot.date}
              />
              {basketUpsell && (
                <>
                  <BasketUpsellSection
                    carousel={basketUpsell.carousel}
                    products={basketUpsell.products}
                    onAddProduct={basketUpsell.onAddProduct}
                    onClickProduct={basketUpsell.onClickProduct}
                    onRemoveProduct={basketUpsell.onRemoveProduct}
                  />
                  <Products
                    products={basketUpsell.selectedProducts}
                    onAddProduct={basketUpsell.onAddProduct}
                    onClickProduct={basketUpsell.onClickProduct}
                    onRemoveProduct={basketUpsell.onRemoveProduct}
                  />
                </>
              )}
            </>
          ) : (
            renderedBasketContent
          )}
          {!isServerDrivenBasket &&
            (isPricingPending ? (
              <OrderSummaryPlaceholder recipeCount={numberOfRecipesInBasket} />
            ) : (
              <OrderSummary
                basketRecipes={basketRecipes}
                deliveryPricing={deliveryPricing}
                isValueBannerEnabled={isValueBannerEnabled}
                numberOfRecipes={numberOfRecipesInBasket}
                shouldShowOrderSummary={shouldShowOrderSummary}
              />
            ))}
          {!isServerDrivenBasket && (
            <DeliveryDetails deliverySlot={deliverySlot} deliveryAddress={deliveryAddress} />
          )}
        </Box>
      </Box>
      {isFlexPortionEditorOpen && <FlexModalOverlay onClick={closePortionEditor} />}
      {visibleErrorToast && (
        <Box
          bottom="112px"
          left="0"
          paddingH={5}
          position={DisplayPosition.Absolute}
          width="100%"
          zIndex={ZIndex.Toast}
        >
          <BasketErrorToast
            key={`${visibleErrorToast.title}-${visibleErrorToast.message}`}
            {...visibleErrorToast}
            onDismiss={dismissErrorToast}
          />
        </Box>
      )}
      <Box
        display={Display.Flex}
        flexShrink={0}
        width="100%"
        justifyContent={JustifyContent.Center}
        boxShadow="0px -2px 4px rgba(0, 0, 0, 0.15)"
        style={{
          position: 'sticky',
          paddingTop: isFlexPortionEditorOpen ? 0 : '1rem',
          paddingBottom: isFlexPortionEditorOpen ? 0 : '1.5rem',
        }}
        bg={Color.White}
        bottom="0"
        left="0"
        right="0"
        zIndex={ZIndex.Top}
      >
        {lastRemoved && (
          <Box
            position={DisplayPosition.Absolute}
            width="400px"
            top="-50px"
            left="50%"
            style={{ transform: 'translateX(-50%)' }}
          >
            <UndoBanner recipe={lastRemoved.recipe} onUndo={handleUndo} />
          </Box>
        )}
        {editingRecipe ? (
          <FlexPortionFooter
            recipeCount={editingRecipe.quantity}
            numPortions={editingRecipe.numPortions}
            onUpdatePortions={handleUpdatePortions}
            onSelectPortions={handleSelectPortions}
            onRemoveRecipe={handleRemoveEditingRecipe}
            mode="editor"
            onCloseEditor={closePortionEditor}
            renderEditor={({ onClose, onRemoveRecipe, onUpdatePortions }) => (
              <FlexPortionEditorBoundary
                deliveryDate={deliverySlot.date}
                isOpen
                menuId={menuUuid ?? ''}
                numPortions={editingRecipe.numPortions}
                onClose={onClose}
                onRemoveRecipe={onRemoveRecipe}
                onSelectPortions={handleSelectPortions}
                onUpdatePortions={onUpdatePortions}
                recipeCount={editingRecipe.quantity}
                recipeId={editingRecipe.recipe.id}
              />
            )}
          />
        ) : (
          <CheckoutButton
            isOverLimitBy={isServerDrivenBasket ? 0 : isOverLimitBy}
            maxRecipesInBox={maxRecipesInBox}
            onCheckout={onCheckout}
            isCheckoutDisabled={
              !!lastRemoved ||
              (isServerDrivenBasket ? isServerCheckoutDisabled : isCheckoutDisabled)
            }
            isCheckoutPending={isCheckoutPending}
            discountLabel={isServerDrivenBasket ? null : discountLabel}
            message={isServerDrivenBasket ? basketContent?.checkout?.message : undefined}
          />
        )}
      </Box>
    </Box>
  )
}
